// src/components/EmptyState.jsx
// Shared "nothing here yet" panel — some pages already had a good version
// of this (icon + heading + subtext + optional action), others had just a
// single bare gray line with no visual weight at all. One component, used
// consistently, covers both.
//
// icon accepts either a lucide component OR a string (e.g. an emoji) so
// this works regardless of whether a given page has migrated off emoji
// icons yet — no coupling to that separate, independently-merged change.
import { T } from '../theme/theme'

export default function EmptyState({ icon, title, subtitle, action, size = 'default', mutedColor = T.mt }) {
  const dims = {
    large:   { icon: 48, title: '18px', subtitle: '14px', pad: '80px 20px' },
    default: { icon: 32, title: '14px', subtitle: '13px', pad: '48px 32px' },
    compact: { icon: 26, title: '14px', subtitle: '12px', pad: '32px 20px' },
  }[size] || { icon: 32, title: '14px', subtitle: '13px', pad: '48px 32px' }
  // `icon` is either a lucide-react component or a plain string/emoji.
  // lucide-react icons (v1.x) are React.forwardRef components — these are
  // OBJECTS shaped like { $$typeof: Symbol(react.forward_ref), render, displayName },
  // not plain functions. `typeof icon === 'function'` is false for them, which
  // previously caused Icon to resolve to null and fall through to the
  // `<span>{icon}</span>` branch below — rendering the forwardRef object itself
  // as a React child (React error #31: "object with keys {$$typeof, render,
  // displayName}"). Treat anything that isn't a string as a component instead.
  const isComponent = icon != null && typeof icon !== 'string'
  const Icon = isComponent ? icon : null

  return (
    <div style={{ textAlign: 'center', padding: dims.pad }}>
      {icon && (
        <div style={{ marginBottom: size === 'compact' ? '10px' : '14px' }}>
          {Icon
            ? <Icon size={dims.icon} strokeWidth={1.5} color={mutedColor} style={{ display: 'inline-block' }} />
            : <span style={{ fontSize: `${dims.icon}px` }}>{icon}</span>}
        </div>
      )}
      {title && (
        <div style={{ fontSize: dims.title, fontWeight: '700', marginBottom: subtitle ? '8px' : 0 }}>
          {title}
        </div>
      )}
      {subtitle && (
        <div style={{ fontSize: dims.subtitle, color: mutedColor, marginBottom: action ? '20px' : 0 }}>
          {subtitle}
        </div>
      )}
      {action}
    </div>
  )
}
